'use client'

import { Button } from '@/shared/ui/button/button'
import * as Dialog from '@radix-ui/react-dialog'
import { X, Phone } from 'lucide-react'
import { motion } from 'framer-motion'

interface ContactModalProps {
  isOpen: boolean
  onClose: () => void
}

// Кастомные иконки для соцсетей
const TelegramIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.333-.373-.12L8.556 13.617l-2.97-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z" />
  </svg>
)

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488" />
  </svg>
)

export const ContactModal = ({ isOpen, onClose }: ContactModalProps) => {
  const contacts = [
    {
      type: 'telegram',
      label: 'Telegram',
      value: '@first_prc',
      href: 'https://t.me/first_prc',
      icon: TelegramIcon,
      bgColor: 'bg-blue-500 hover:bg-blue-600',
      textColor: 'text-blue-600',
      bgLight: 'bg-blue-50 dark:bg-blue-900/10'
    },
    {
      type: 'whatsapp',
      label: 'WhatsApp',
      value: '+7 (925) 404-09-99',
      href: 'https://wa.me/79254040999',
      icon: WhatsAppIcon,
      bgColor: 'bg-green-500 hover:bg-green-600',
      textColor: 'text-green-600',
      bgLight: 'bg-green-50 dark:bg-green-900/10'
    },
    {
      type: 'phone',
      label: 'Телефон',
      value: '+7 (925) 404-09-99',
      href: 'tel:+79254040999',
      icon: Phone,
      bgColor: 'bg-orange-500 hover:bg-orange-600',
      textColor: 'text-orange-600',
      bgLight: 'bg-orange-50 dark:bg-orange-900/10'
    }
  ]

  const handleContentClick = (e: React.MouseEvent) => {
    // Закрываем модальное окно только если клик был на фон, а не на контент
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/50" />
        <Dialog.Content
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={handleContentClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative bg-background rounded-xl border shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Заголовок */}
            <div className="flex items-center justify-between p-6 border-b">
              <Dialog.Title className="text-xl font-semibold text-foreground">
                Связаться с нами
              </Dialog.Title>
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <X className="h-4 w-4" />
                </Button>
              </Dialog.Close>
            </div>

            {/* Контакты */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground mb-6">
                Выберите удобный способ связи с нашими специалистами
              </p>

              {contacts.map((contact) => {
                const Icon = contact.icon
                return (
                  <motion.a
                    key={contact.type}
                    href={contact.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block p-4 rounded-lg border transition-all duration-200 hover:shadow-md ${contact.bgLight} hover:border-current`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-full text-white ${contact.bgColor}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-medium ${contact.textColor}`}>{contact.label}</h4>
                        <p className="text-sm text-muted-foreground">{contact.value}</p>
                      </div>
                    </div>
                  </motion.a>
                )
              })}
            </div>

            {/* Дополнительная информация */}
            <div className="px-6 pb-6">
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-xs text-muted-foreground text-center">
                  Время работы: Пн-Пт с 9:00 до 18:00 (МСК)
                </p>
              </div>
            </div>
          </motion.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
